/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * ============================================================================
 * THE CROSS-MODULE READ CONTRACT  (MODULES.md rules 2, 4 and 6)
 * ============================================================================
 * Incentive goals are scored against real sold-through, which lives in the
 * `depletions` table — owned by the **depletions-inventory** module. This module
 * may not import that module and may not read that table. It calls two
 * security-definer RPCs **by name** instead, exactly as RESOLVER_DESIGN.md
 * specified and as migration 0070 built them:
 *
 *   depletions_outlet_rollup(p_account_id, p_start, p_end, p_distributor, p_chain)
 *     → rows of { outlet_id, outlet_name, chain, city, state, cases,
 *                 first_month, last_month }  — door-level, whole months that
 *       OVERLAP the window (0070's stated default for partial months).
 *
 *   depletions_outlet_count(p_account_id, p_start, p_end, p_distributor)
 *     → integer count of distinct doors that sold in the window.
 *
 * Both re-check `is_account_member` internally (security definer bypasses RLS),
 * so a caller cannot read another tenant's depletions by passing a foreign
 * account id. This file is the ONLY place in the module that names them.
 *
 * Every function here returns `{ ok, rows|value, reason }` rather than throwing:
 * a tenant that hasn't bought or populated Depletions & Inventory is a normal
 * state, not an error, and the resolver has to be able to say "no source" in
 * plain language instead of blowing up a scoring screen.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

/** ISO yyyy-mm-dd for a Date, in local time (dates here are calendar dates). */
function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * The 12 calendar months ending the day before `startDate` — the baseline a
 * "new point of distribution" is new *against*. Returns { start, end } as ISO
 * dates, or null when startDate isn't a usable date.
 */
export function baselineWindow(startDate) {
  if (!startDate) return null;
  const start = new Date(`${String(startDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setDate(end.getDate() - 1);
  const from = new Date(end);
  from.setFullYear(from.getFullYear() - 1);
  from.setDate(from.getDate() + 1);
  return { start: iso(from), end: iso(end) };
}

/**
 * Door-level depletion rollup for a window.
 * @returns {Promise<{ok:boolean, rows:Array, reason?:string}>}
 */
export async function outletRollup({ accountId, start, end, distributor = null, chain = null }) {
  if (!accountId) return { ok: false, rows: [], reason: "no_account" };
  if (!start || !end) return { ok: false, rows: [], reason: "no_window" };
  const { data, error } = await supabase.rpc("depletions_outlet_rollup", {
    p_account_id: accountId,
    p_start: String(start).slice(0, 10),
    p_end: String(end).slice(0, 10),
    p_distributor: distributor || null,
    p_chain: chain || null,
  });
  if (error) {
    // 42883 = the function isn't there (migration 0070 not applied on this
    // project). Anything else is a permission/entitlement problem. Either way
    // the honest answer to the user is "no automatic source", not a stack trace.
    console.error("[incentive.depletionsContract.outletRollup]", error);
    return { ok: false, rows: [], reason: error.code === "42883" ? "no_source" : "source_unavailable" };
  }
  return { ok: true, rows: data ?? [] };
}

/**
 * Distinct doors that sold in a window. Kept because it is the cheap answer when
 * only the count matters (the distributor-progress rollup); `outletRollup` is
 * used where the ids themselves are needed (new-door deltas).
 * @returns {Promise<{ok:boolean, value:number, reason?:string}>}
 */
export async function outletCount({ accountId, start, end, distributor = null }) {
  if (!accountId) return { ok: false, value: 0, reason: "no_account" };
  if (!start || !end) return { ok: false, value: 0, reason: "no_window" };
  const { data, error } = await supabase.rpc("depletions_outlet_count", {
    p_account_id: accountId,
    p_start: String(start).slice(0, 10),
    p_end: String(end).slice(0, 10),
    p_distributor: distributor || null,
  });
  if (error) {
    console.error("[incentive.depletionsContract.outletCount]", error);
    return { ok: false, value: 0, reason: error.code === "42883" ? "no_source" : "source_unavailable" };
  }
  return { ok: true, value: Number(data) || 0 };
}

/**
 * The doors a program's distributor has actually sold through, for the target-
 * account picker (item 233). Looks back `months` from today AND across the
 * program window, so a program dated in the future still offers real accounts.
 * Returns [{ id, name, chain, city, state, cases }] sorted by name.
 */
export async function listOutletsForPicker({ accountId, distributor = null, start = null, end = null, months = 24 }) {
  const today = new Date();
  const lookbackStart = new Date(today);
  lookbackStart.setMonth(lookbackStart.getMonth() - months);

  const from = [iso(lookbackStart), start].filter(Boolean).sort()[0];
  const to = [iso(today), end].filter(Boolean).sort().slice(-1)[0];

  const res = await outletRollup({ accountId, start: from, end: to, distributor });
  if (!res.ok) return { ok: false, outlets: [], reason: res.reason };

  const byId = new Map();
  for (const r of res.rows) {
    if (!r?.outlet_id) continue;
    const prev = byId.get(r.outlet_id);
    const cases = Number(r.cases) || 0;
    if (prev) { prev.cases += cases; continue; }
    byId.set(r.outlet_id, {
      id: r.outlet_id,
      name: r.outlet_name || "Unnamed account",
      chain: r.chain || "",
      city: r.city || "",
      state: r.state || "",
      cases,
    });
  }
  const outlets = [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  return { ok: true, outlets };
}
